import { useEffect } from 'react'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
} from '@heroicons/react/solid'
import { classes, throwUnsupportedValue } from '../utils'
import type { ToastPayload } from './types'

type Props = ToastPayload & {
  onClose: (id: string) => void
}

export default function Toast(props: Props) {
  useEffect(() => {
    const timeoutId = setTimeout(
      // workaround for react hooks warning as otherwise props is passed as this
      () => props.onClose.call(undefined, props.id),
      props.timeout,
    )

    return () => {
      clearTimeout(timeoutId)
    }
  }, [props.onClose, props.id, props.timeout])

  return (
    <div
      onClick={() => {
        if (props.canClose) {
          props.onClose(props.id)
        }
      }}
      className={classes(
        'flex flex-row items-center p-4 my-4 text-sm bg-white rounded shadow-md w-80 border-2',
        'hover:shadow-xl',
        getToastBorder(props.type),
        props.canClose ? 'cursor-pointer' : 'cursor-auto',
      )}
    >
      {getToastIcon(props.type)}

      <div className="flex-1 ml-4">{props.message}</div>
    </div>
  )
}

function getToastBorder(type: ToastPayload['type']) {
  switch (type) {
    case 'error':
      return 'border-red-500'
    case 'info':
      return 'border-blue-500'
    case 'success':
      return 'border-green-500'
    case 'warning':
      return 'border-yellow-500'
    default:
      throwUnsupportedValue(type)
  }
}

function getToastIcon(type: ToastPayload['type']) {
  const sharedClasses = 'h5 w-5'

  switch (type) {
    case 'error':
      return (
        <ExclamationCircleIcon
          aria-hidden
          className={classes(sharedClasses, 'text-red-500')}
        />
      )
    case 'info':
      return (
        <InformationCircleIcon
          aria-hidden
          className={classes(sharedClasses, 'text-blue-500')}
        />
      )
    case 'success':
      return (
        <CheckCircleIcon
          aria-hidden
          className={classes(sharedClasses, 'text-green-500')}
        />
      )
    case 'warning':
      return (
        <ExclamationIcon
          aria-hidden
          className={classes(sharedClasses, 'text-yellow-500')}
        />
      )
    default:
      throwUnsupportedValue(type)
  }
}
